/* @flow */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import invert from 'invert-color';
import { removeTag } from '../../reduxStore/modules/tags/actions';
import TagCustomersPicker from '../Feed/TagCustomersPicker';
import { log } from '../../utils/jsUtils';
import { useRemoveTagFromCustomerMutation } from '../../features/api/tagsApi';
import { selectRequests, updateRequests } from '../../reduxStore/modules/feed';
import { setSnackMessage } from '../../reduxStore/modules/common';
import { selectUser } from '../../reduxStore/modules/user';
import * as R from 'ramda';
import { useTheme } from '@mui/material/styles';
import tagReadonlyStyles from './Styles/TagReadonly.Style';

// Enable debug logging for this component
const DEBUG_ENABLED = true;

type TagType = {
  _id: string,
  name?: string,
  color?: string,
  TagName?: string,
  Name?: string,
  Color?: string
};

type Props = {
  tag: TagType,
  customerId?: string,
  onRemove?: Function,
  size?: 'small' | 'medium',
  variant?: 'default' | 'outlined',
  onTagClick?: Function,
  readonly?: boolean,
  debug?: boolean,
  onTagRemoved?: Function
};

const TagReadonly = ({
  tag,
  customerId,
  onRemove,
  size = 'medium',
  variant = 'default',
  onTagClick,
  readonly = false,
  debug = false,
  onTagRemoved
}: Props) => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [anchorEl, setAnchorEl] = useState(null);
  const [localIsOpen, setLocalIsOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const tagRef = useRef(null);
  
  // Store tag ID in ref to avoid dependency in logDebug
  const tagIdRef = useRef(tag?._id);
  useEffect(() => {
    tagIdRef.current = tag?._id;
  }, [tag?._id]);
  
  // RTK Query mutation for backend removal
  const [removeTagFromCustomer] = useRemoveTagFromCustomerMutation();
  
  // Enhanced debug logging - with stable dependencies
  const logDebug = useCallback((message, ...args) => {
    if (DEBUG_ENABLED || debug) {
      const tagIdPrefix = tagIdRef.current?.slice(0, 6) || 'unknown';
      log(`[TagReadonly:${tagIdPrefix}] ${message}`, ...args);
    }
  }, [debug]); // No tag dependency to avoid re-renders
  
  // Handle tag removal with direct DOM manipulation
  const handleRemove = useCallback(async (event) => {
    // Stop event propagation
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    // Guard clauses for safety - allow removal if we have customerId OR onTagRemoved callback
    const canRemove = !readonly && !isRemoving && tag?._id && tagRef.current && (customerId || onTagRemoved);
    if (!canRemove) {
      logDebug('Removal aborted - invalid state', {
        readonly,
        isRemoving,
        hasCustomerId: !!customerId,
        hasOnTagRemoved: !!onTagRemoved,
        hasTagId: !!tag?._id,
        hasTagRef: !!tagRef.current
      });
      return;
    }

    try {
      // Set flag to prevent multiple clicks
      setIsRemoving(true);
      logDebug('Starting tag removal', { tagId: tag._id, customerId, standaloneMode: !customerId });

      // 1. Immediately hide the tag element with animation
      const element = tagRef.current;
      element.style.transition = 'opacity 150ms ease-out, transform 150ms ease-out';
      element.style.opacity = '0';
      element.style.transform = 'scale(0.8)';

      // 2. Immediately notify parent that tag was visually removed
      // This is critical - do this BEFORE the API call so UI updates instantly
      if (onTagRemoved) {
        logDebug('Calling onTagRemoved callback with full tag object');
        // Pass the full tag object to the parent
        onTagRemoved(tag);
      }

      // After animation completes, hide the element completely
      setTimeout(() => {
        if (element) {
          element.style.display = 'none';
        }
      }, 150);

      // 3. Only perform backend removal if we have a customerId (customer-bound mode)
      // In standalone mode (no customerId), just call the callback above
      if (customerId) {
        try {
          logDebug('Executing removeTagFromCustomer mutation');
          const result = await removeTagFromCustomer({
            tagId: tag._id,
            customerIds: [customerId],
            user,
            showNotification: true
          }).unwrap();

          logDebug('Tag removal successful', result);

          // Success message is now handled in the API slice
        } catch (error) {
          // Error in API call - restore tag visibility
          logDebug('Error removing tag', error);

          if (element) {
            element.style.display = 'inline-flex';
            element.style.opacity = '1';
            element.style.transform = 'scale(1)';
          }

          dispatch(setSnackMessage({
            message: error.data || 'Error removing tag',
            level: 'error',
            duration: 3000
          }));
        }
      }

      setIsRemoving(false);
    } catch (e) {
      // Handle unexpected errors
      logDebug('Unexpected error during tag removal', e);

      if (tagRef.current) {
        tagRef.current.style.display = 'inline-flex';
        tagRef.current.style.opacity = '1';
        tagRef.current.style.transform = 'scale(1)';
      }

      setIsRemoving(false);

      dispatch(setSnackMessage({
        message: 'Error removing tag',
        level: 'error',
        duration: 3000
      }));
    }
  }, [customerId, tag, readonly, isRemoving, removeTagFromCustomer, user, dispatch, onTagRemoved, logDebug]);
  
  // Handle click on the tag
  const handleClick = useCallback((event) => {
    if (onTagClick) {
      onTagClick(event);
      return;
    }
    
    if (readonly) {
      return;
    }
    
    if (customerId) {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
      setLocalIsOpen(true);
    }
  }, [customerId, onTagClick, readonly]);
  
  // Handle closing the tag picker
  const handleTagPickerClose = useCallback(() => {
    setLocalIsOpen(false);
    setAnchorEl(null);
  }, []);
  
  const tagName = tag?.name || tag?.TagName || tag?.Name || '';
  const theme = useTheme();
  
  return (
    <>
      <div 
        ref={tagRef}
        style={tagReadonlyStyles.tagContainer(theme, { tag, size, variant, isRemoving })}
        onClick={handleClick}
        data-tag-id={tag?._id}
        data-customer-id={customerId}
        data-tag-name={tagName}
      >
        <span style={tagReadonlyStyles.tagName}>{tagName}</span>
        {/* Show remove 'x' if not readonly AND we have customerId or onTagRemoved callback */}
        {!readonly && (customerId || onTagRemoved) && (
          <span
            style={tagReadonlyStyles.removeIcon(theme, size)}
            onClick={handleRemove}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.3'; }}
          >
            ×
          </span>
        )}
      </div>
      
      {/* Tag picker popup when clicking on a tag */}
      {localIsOpen && customerId && !readonly && (
        <TagCustomersPicker
          customerId={customerId}
          anchorEl={anchorEl}
          onClose={handleTagPickerClose}
          debug={DEBUG_ENABLED || debug}
        />
      )}
    </>
  );
};

export default TagReadonly;